import { useState } from 'react';
import { Plus, X, DollarSign } from 'lucide-react';

interface FeatureInput {
  name: string;
  price: number;
}

interface ItemInputProps {
  features: FeatureInput[];
  numParticipants: number;
  onFeaturesChange: (features: FeatureInput[]) => void;
  onParticipantsChange: (num: number) => void;
  onStart: () => void;
  /** Share of the total price handed out as budget (0–1). */
  budgetPercentage?: number;
}

export function ItemInput({
  features,
  numParticipants,
  onFeaturesChange,
  budgetPercentage = 0.4,
  onParticipantsChange,
  onStart,
}: ItemInputProps) {
  const [nameInput, setNameInput] = useState('');
  const [priceInput, setPriceInput] = useState('');

  const handleAdd = () => {
    const trimmedName = nameInput.trim();
    const price = parseFloat(priceInput);
    
    if (trimmedName && !isNaN(price) && price > 0) {
      if (!features.some(f => f.name === trimmedName)) {
        onFeaturesChange([...features, { name: trimmedName, price }]);
        setNameInput('');
        setPriceInput('');
      }
    }
  };

  const handleRemove = (index: number) => {
    onFeaturesChange(features.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const totalPrice = features.reduce((sum, f) => sum + f.price, 0);
  const totalBudget = totalPrice * budgetPercentage;
  const budgetPct = Math.round(budgetPercentage * 100);
  const budgetPerParticipant = numParticipants > 0 ? totalBudget / numParticipants : 0;

  const canStart = features.length >= 1 && numParticipants > 0 && totalPrice > 0;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">
          Set Up Buy A Feature
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Enter features with prices and set the number of participants. Each participant will receive an equal share of the total budget ({budgetPct}% of the total price of all features).
        </p>

        {/* Number of Participants */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
            Number of Participants
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              min="1"
              max="20"
              value={numParticipants}
              onChange={(e) => onParticipantsChange(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
              className="w-24 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm text-[var(--text-sec)]">
              {numParticipants} participant{numParticipants !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Budget Summary */}
        {features.length > 0 && (
          <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-[var(--text-ter)]" />
              <h3 className="font-semibold text-[var(--text-sec)]">Budget Summary</h3>
            </div>
            <div className="space-y-1 text-sm text-[var(--text-sec)]">
              <div>Total price of all features: <strong>${totalPrice.toFixed(2)}</strong></div>
              <div>Total budget ({budgetPct}% of total): <strong>${totalBudget.toFixed(2)}</strong></div>
              <div>Budget per participant: <strong>${budgetPerParticipant.toFixed(2)}</strong></div>
            </div>
          </div>
        )}

        {/* Feature Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
            Add Features
          </label>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Feature name..."
              className="flex-1 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Price..."
              className="w-32 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleAdd}
              disabled={!nameInput.trim() || !priceInput || parseFloat(priceInput) <= 0}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add
            </button>
          </div>
        </div>

        {/* Features List */}
        {features.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-[var(--text-sec)] mb-3">
              Features ({features.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-[var(--surface)] border border-[var(--rule)]"
                >
                  <div className="flex-1">
                    <span className="text-[var(--text-pri)] font-medium">{feature.name}</span>
                    <span className="text-[var(--text-sec)] ml-2">${feature.price.toFixed(2)}</span>
                  </div>
                  <button
                    onClick={() => handleRemove(index)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={onStart}
          disabled={!canStart}
          className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed font-medium text-lg transition-colors"
        >
          {canStart
            ? `Start Purchasing (${features.length} feature${features.length !== 1 ? 's' : ''}, ${numParticipants} participant${numParticipants !== 1 ? 's' : ''})`
            : 'Add at least 1 feature and set number of participants'}
        </button>
      </div>
    </div>
  );
}


